import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ComunicadoService } from 'app/shared/services/edduca-imob';
import { ImobiliariaService } from 'app/shared/services/edduca-imob/imobiliaria/imobiliaria.service';
import { UsuarioService } from 'app/shared/services/edduca-imob/usuario/usuario.service';
import { ImobiliariaForAutocompleteResponse } from 'app/shared/models/ei-models';
import { UsuarioListResponse } from 'app/shared/services/edduca-imob/usuario/response/usuario-list.response';
import { PerfilEnum } from 'app/shared/models/core/security/perfil.enum';
import { TipoDestinoEnum } from 'app/shared/models/ei-models/comunicado/request/comunicado.request';
import { NotificationService } from 'app/shared/components/notification';

interface DialogData {
  perfilId: number;
  imobiliariaId: number;
  usuarioLogadoId: number;
}

@Component({
  selector: 'app-comunicado-register',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './comunicado-register.component.html',
  styleUrl: './comunicado-register.component.scss'
})
export class ComunicadoRegisterComponent implements OnInit {
  private _fb = inject(FormBuilder);
  private _comunicadoService = inject(ComunicadoService);
  private _imobiliariaService = inject(ImobiliariaService);
  private _usuarioService = inject(UsuarioService);
  private _notification = inject(NotificationService);

  form: FormGroup;
  imagemBase64?: string;
  imagemNome?: string;
  salvando = false;

  // Dados recebidos do componente pai
  perfilId: number = 0;
  imobiliariaId: number = 0;
  usuarioLogadoId: number = 0;

  // Tipo de destino selecionado
  tipoDestinoSelecionado: TipoDestinoEnum = TipoDestinoEnum.TODOS;

  // Opções de destino para ADM_SISTEMA
  opcoesDestinoAdmSistema = [
    { id: TipoDestinoEnum.TODOS, nome: 'Todos os usuários', icone: 'groups' },
    { id: TipoDestinoEnum.IMOBILIARIA, nome: 'Imobiliária específica', icone: 'business' },
    { id: TipoDestinoEnum.USUARIO, nome: 'Usuário específico', icone: 'person' }
  ];

  // Opções de destino para ADM_IMOBILIARIA
  opcoesDestinoAdmImobiliaria = [
    { id: TipoDestinoEnum.TODOS, nome: 'Todos da imobiliária', icone: 'groups' },
    { id: TipoDestinoEnum.USUARIO, nome: 'Usuário específico', icone: 'person' }
  ];

  // Autocomplete de imobiliárias (para ADM_SISTEMA)
  imobiliariaCtrl = new FormControl<ImobiliariaForAutocompleteResponse | null>(null);
  imobiliariaOptions: ImobiliariaForAutocompleteResponse[] = [];
  filteredImobiliarias: ImobiliariaForAutocompleteResponse[] = [];

  // Lista de usuários (para seleção de usuário específico)
  usuarios: UsuarioListResponse[] = [];
  loadingUsuarios = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private dialogRef: MatDialogRef<ComunicadoRegisterComponent>
  ) {
    this.form = this._fb.group({
      titulo: ['', [Validators.required, Validators.maxLength(100)]],
      textoComunicado: ['', [Validators.required, Validators.maxLength(1000)]],
      tipoDestino: [TipoDestinoEnum.TODOS, Validators.required],
      usuarioDestinoId: [null]
    });
  }

  ngOnInit(): void {
    // Pega dados do dialog
    this.perfilId = this.data?.perfilId || 0;
    this.imobiliariaId = this.data?.imobiliariaId || 0;
    this.usuarioLogadoId = this.data?.usuarioLogadoId || 0;

    // Se for ADM_SISTEMA, carrega lista de imobiliárias
    if (this.perfilId === PerfilEnum.ADM_SISTEMA) {
      this.loadImobiliarias();
    }

    // Se for ADM_IMOBILIARIA, carrega usuários da imobiliária
    if (this.perfilId === PerfilEnum.ADM_IMOBILIARIA) {
      this.carregarUsuariosImobiliaria(this.imobiliariaId);
    }

    // Escuta mudanças no tipo de destino
    this.form.get('tipoDestino')?.valueChanges.subscribe(tipo => {
      this.tipoDestinoSelecionado = tipo;

      // Limpa seleções anteriores
      this.form.get('usuarioDestinoId')?.setValue(null);
      this.imobiliariaCtrl.setValue(null);

      // Se ADM_SISTEMA selecionar imobiliária específica, precisa carregar usuários depois
      if (this.perfilId === PerfilEnum.ADM_SISTEMA && tipo === TipoDestinoEnum.USUARIO) {
        // Usuários serão carregados após selecionar imobiliária
        this.usuarios = [];
      }
    });
  }

  /**
   * Getter para verificar se é ADM_SISTEMA
   */
  get isAdmSistema(): boolean {
    return this.perfilId === PerfilEnum.ADM_SISTEMA;
  }

  /**
   * Getter para verificar se é ADM_IMOBILIARIA
   */
  get isAdmImobiliaria(): boolean {
    return this.perfilId === PerfilEnum.ADM_IMOBILIARIA;
  }

  /**
   * Getter para opções de destino baseado no perfil
   */
  get opcoesDestino() {
    return this.isAdmSistema ? this.opcoesDestinoAdmSistema : this.opcoesDestinoAdmImobiliaria;
  }

  /**
   * Getter para obter a opção de destino selecionada
   */
  get opcaoDestinoSelecionada() {
    const tipoDestino = this.form.get('tipoDestino')?.value;
    return this.opcoesDestino.find(o => o.id === tipoDestino) || null;
  }

  /**
   * Carrega lista de imobiliárias para o autocomplete
   */
  loadImobiliarias(): void {
    this._imobiliariaService.getAllForAutocomplete().subscribe({
      next: (imobiliarias) => {
        this.imobiliariaOptions = imobiliarias;
        this.filteredImobiliarias = imobiliarias;
      },
      error: () => {
        this._notification.error('Falha ao carregar lista de imobiliárias');
      }
    });
  }

  /**
   * Filtra imobiliárias no autocomplete
   */
  filterImobiliarias(event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredImobiliarias = this.imobiliariaOptions.filter(imob =>
      imob.nome.toLowerCase().includes(value)
    );
  }

  /**
   * Exibe nome da imobiliária no input
   */
  displayImobiliariaFn(imobiliaria: ImobiliariaForAutocompleteResponse): string {
    return imobiliaria?.nome || '';
  }

  /**
   * Callback quando uma imobiliária é selecionada (ADM_SISTEMA)
   */
  onImobiliariaSelected(imobiliaria: ImobiliariaForAutocompleteResponse): void {
    // Se o tipo destino for USUARIO, carrega os usuários dessa imobiliária
    if (this.tipoDestinoSelecionado === TipoDestinoEnum.USUARIO) {
      this.carregarUsuariosImobiliaria(imobiliaria.id);
    }
  }

  /**
   * Carrega usuários de uma imobiliária
   */
  carregarUsuariosImobiliaria(imobiliariaId: number): void {
    this.loadingUsuarios = true;
    this._usuarioService.getUsuariosImobiliaria(imobiliariaId).subscribe({
      next: (usuarios) => {
        // Filtra o usuário logado da lista
        this.usuarios = (usuarios || []).filter(u => Number(u.id) !== this.usuarioLogadoId);
        this.loadingUsuarios = false;
      },
      error: () => {
        this.usuarios = [];
        this.loadingUsuarios = false;
        this._notification.error('Erro ao carregar usuários.');
      }
    });
  }

  onFileChange(evt: Event): void {
    const input = evt.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;
    const file = input.files[0];
    this.imagemNome = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      this.imagemBase64 = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  salvar(): void {
    if (this.form.invalid) return;

    // Validações adicionais
    const tipoDestino = this.form.get('tipoDestino')?.value;

    if (this.isAdmSistema) {
      if (tipoDestino === TipoDestinoEnum.IMOBILIARIA && !this.imobiliariaCtrl.value) {
        this._notification.warn('Selecione uma imobiliária.');
        return;
      }
      if (tipoDestino === TipoDestinoEnum.USUARIO) {
        if (!this.imobiliariaCtrl.value) {
          this._notification.warn('Selecione uma imobiliária primeiro.');
          return;
        }
        if (!this.form.get('usuarioDestinoId')?.value) {
          this._notification.warn('Selecione um usuário.');
          return;
        }
      }
    }

    if (this.isAdmImobiliaria && tipoDestino === TipoDestinoEnum.USUARIO && !this.form.get('usuarioDestinoId')?.value) {
      this._notification.warn('Selecione um usuário.');
      return;
    }

    this.salvando = true;

    const payload = {
      titulo: this.form.get('titulo')?.value,
      textoComunicado: this.form.get('textoComunicado')?.value,
      usuarioEnvioId: this.usuarioLogadoId,
      tipoDestino: tipoDestino,
      imobiliariaId: this.isAdmSistema
        ? (this.imobiliariaCtrl.value?.id || null)
        : this.imobiliariaId,
      usuarioDestinoId: this.form.get('usuarioDestinoId')?.value || null,
      imagemBase64: this.imagemBase64 ?? null,
      imagemNome: this.imagemNome ?? null
    };

    this._comunicadoService.criarComunicado(payload).subscribe({
      next: () => {
        // Atualiza a contagem de não lidos na navbar (para o destinatário ver quando logar)
        this._comunicadoService.refreshUnreadCount(this.usuarioLogadoId);
        this._notification.success('Comunicado enviado com sucesso!');
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.salvando = false;
        this._notification.error(err?.error?.message || 'Erro ao enviar comunicado.');
      }
    });
  }

  cancelar(): void {
    this.dialogRef.close(false);
  }
}